import { Component, EventEmitter, Inject } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { Observable, of } from 'rxjs'
import { CurrencyAmount, Role } from '../../../../../graphql'
import { RoleManager } from '../../../../injectable'
import { WhiteLabelService } from '../../../../route/account/white-label-settings/white-label.service'
import { formatDate } from '../../../../utility'

interface Data {
    travelDates: {
        checkin: string,
        checkout: string,
        nights: number
    },
    totalGuests: number,
    listingId: string,
    listingName: string,
    commissionRate: string,
    commissionAmount$: Observable<any>,
    adjustedPrice: CurrencyAmount,
    price: string,
    priceInListingCurrency: string,
    ZARListing: Boolean,
    installmentSchedule?: {
        installmentScheduleId: string,
        installments: Array<{
            installmentScheduleId: string,
            installmentId: string,
            daysPriorTo: number | string | null,
            percentage: number
        }>
    },
    consumerView: boolean
}

@Component({
    selector: 'app-request-to-book-summary',
    templateUrl: './request-to-book-summary.html'
})
export class RequestToBookSummaryComponent {

    loadingNextStep = false;
    checkin: string;
    checkout: string;
    $requestToBookConfirmed = new EventEmitter<Boolean>();
    requestToBookSuccess = false;
    requestToBookFail = false;
    
    isWhiteLabelConsumer = false;
    agentName: string = '';

    constructor(
        private _router: Router,
        private _dialogRef: MatDialogRef<RequestToBookSummaryComponent>,
        private _whiteLabelService: WhiteLabelService,
        private _roleManager: RoleManager,
        @Inject(MAT_DIALOG_DATA) public data: Data) {

        this.checkin = formatDate(data.travelDates.checkin);
        this.checkout = formatDate(data.travelDates.checkout);
        console.log('RequestToBookSummary data:', data);
        console.log('Installment Schedule:', data.installmentSchedule);
        
        // Setup white label consumer status
        this.isWhiteLabelConsumer = this._whiteLabelService.isWhiteLabelEnabled() &&
                                   this._roleManager.hasRole(Role.Consumer);
        
        if (this.isWhiteLabelConsumer) {
            const config = this._whiteLabelService.getCurrentConfig();
            this.agentName = 'your travel agent';
        }
    }

    getInstallmentAmount(percentage: number): string {
        if (!this.data?.adjustedPrice) {
            console.warn('AdjustedPrice is not available for installment calculation')
            return ''
        }

        // Calculate amount from micros
        const baseAmount = this.data.adjustedPrice.amountMicros / 1_000_000
        const amount = (baseAmount * percentage) / 100

        // Format based on currency
        return amount.toLocaleString('en-US', {
            style: 'currency',
            currency: this.data.adjustedPrice.currency,
            maximumFractionDigits: 0
        })
    }

    formatDaysPrior(days: number | string | null): string {
        if (!days) return 'upon confirmation'

        // Handle ISO 8601 duration format (e.g., "P90D")
        if (typeof days === 'string' && days.startsWith('P')) {
            const daysNum = parseInt(days.replace(/P(\d+)D/, '$1'))
            return `${daysNum} days before check-in`
        }

        // Handle numeric input
        const daysNum = typeof days === 'string' ? parseInt(days) : days

        if (daysNum === 0) return 'upon confirmation'
        return `${daysNum} days before check-in`
    }

    confirm() {
        this.setLoadingState(true)
        this.$requestToBookConfirmed.emit(true)
    }

    decline() {
        this._dialogRef.close(false)
    }

    success() {
        this.setLoadingState(false)
        this.requestToBookSuccess = true
    }

    fail() {
        this.setLoadingState(false)
        this.requestToBookFail = true
    }

    viewTrips() {
        this.decline()
        of(this._router.navigateByUrl('/trips')).subscribe()
    }

    setLoadingState(value: boolean) {
        this.loadingNextStep = value
    }

    /**
     * Determines if this installment needs to be paid at booking time
     * @param daysPriorTo The days prior to checkin for this installment
     * @returns boolean indicating if payment is due now
     */
    isPaymentDueNow(daysPriorTo: number | string | null): boolean {
        if (!daysPriorTo || daysPriorTo === 'P0D') return true;
        
        if (typeof daysPriorTo === 'string' && daysPriorTo.startsWith('P')) {
            // Convert ISO duration to number of days
            const daysNum = parseInt(daysPriorTo.replace(/P(\d+)D/, '$1'));
            daysPriorTo = daysNum;
        }

        // If we don't have checkin date, assume nothing is due
        if (!this.data?.travelDates?.checkin) return false;

        const today = new Date();
        const checkin = new Date(this.data.travelDates.checkin);
        const daysToCheckin = Math.ceil((checkin.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        // If days until checkin is less than or equal to the payment threshold, payment is due now
        return daysToCheckin <= Number(daysPriorTo);
    }

    /**
     * Gets the total percentage due at booking time
     */
    getTotalPercentageDueNow(): number {
        if (!this.data?.installmentSchedule?.installments) return 0;
        
        return this.data.installmentSchedule.installments
            .filter(installment => this.isPaymentDueNow(installment.daysPriorTo))
            .reduce((sum, installment) => sum + installment.percentage, 0);
    }

    readonly Number = Number
}
